import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";

import DeleteConfirmPopUp from "../../common/deleteConfirmPopUp";
import CreateCoursePopup from "../../common/createPopup";
import PagesComponent from "../../common/PagesComponent";
import MarkdownViewer from "../../common/MarkdownViewer";
import MCQViewer from "../../common/MCQViewer";
import TrueFalseViewer from "../../common/TrueFalseViewer";
import MatchViewer from "../../common/MatchViewer";
import FillUpsViewer from "../../common/FillUpsViewer";
import AnalysisComponent from "../../common/AnalysisComponent";
import ContentGeneratorPopup from "../../common/ContentGeneratorPopup";
import MarkdownEditorPopup from "../../common/MarkdownEditorPopup";
import ContentToolbarWrapperComponent from "../../common/ContentToolbarWrapper";
import ActionButtons from "../../common/ActionButtons";
import EvalManualPopup from "../../common/EvalManualPopup";
import EvalScoreDetailsPopup from "../../common/EvalScoreDetailsPopup";
import checkImg from "../../../assets/image/checkImage.png";
import ViewMaterialPopup from "../../common/ViewMaterialPopup";
import MaterialViewer from "../../common/MaterialViewer";
import UploadMaterialPopup from "../../common/UploadMaterialPopup";

import {
  getSubtopicsByTopicKey,
  getPagesForTopic,
  getPageForTopic,
  updateSubtopic,
  deleteSubtopic,
  uploadPageContentFile,
  removePageContentFile,
} from "../../../helper/apicalls/course";
import {
  getTopicPageEvalStatus,
  getTopicEvalPageContent,
  evalWithAiMark,
  createTopicEvalManually,
} from "../../../helper/apicalls/eval";
import "./../../../styles/admin/topics.scss";
import GlobalLoader from "../../common/GlobalLoader";
import EmptyPage from "../../common/emptyPage";
import Cookies from "js-cookie";
import SuccessPopup from "../../common/SuccessPopup";

const TopicDetails = () => {
  const { key, subkey, topicKey } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [subTopics, setSubTopics] = useState([]);
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isManualEvalOpen, setIsManualEvalOpen] = useState(false);
  const [manualEvalSaving, setManualEvalSaving] = useState(false);
  const [isEvalScorePopupOpen, setIsEvalScorePopupOpen] = useState(false);

  const [selectedPage, setSelectedPage] = useState(null);
  const [selectedChildMaterial, setSelectedChildMaterial] = useState(null);
  const [removeCache, setRemoveCache] = useState(false);

  const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = useState(false);
  const [openPopup, setOpenPopup] = useState(false);
  const [popupHeaderTitle, setPopupHeaderTitle] = useState("");
  const [popupBtnText, setPopupBtnText] = useState("");
  const [popupInitialValue, setPopupInitialValue] = useState("");
  const [selectedSubTopic, setSelectedSubTopic] = useState(null);

  const [isGeneratorOpen, setIsGeneratorOpen] = useState(false);
  const [isReplaceMode, setIsReplaceMode] = useState(false);
  const [addAfterIndex, setAddAfterIndex] = useState(null);
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [isUploadMaterialOpen, setIsUploadMaterialOpen] = useState(false);
  const [isMaterialPopupOpen, setIsMaterialPopupOpen] = useState(false);
  const [selectedMaterialForPopup, setSelectedMaterialForPopup] =
    useState(null);

  const isEvalPath = location.pathname.startsWith("/admin/eval");

  const role = Cookies.get("role");
  const permissions = Cookies.get("permissions");
  const permissionsData = permissions ? JSON.parse(permissions) : [];
  const [isAudioGenerated, setIsAudioGenerated] = useState(false);

  const fetchSubTopics = async () => {
    try {
      setLoading(true);
      const data = await getSubtopicsByTopicKey(topicKey);

      if (Array.isArray(data)) {
        setSubTopics(data);
      } else if (
        data &&
        typeof data === "object" &&
        data.hasOwnProperty("id")
      ) {
        setSubTopics([data]);
      } else {
        setSubTopics([]);
      }
    } catch (err) {
      console.error("Error fetching subtopics:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleEvalManuallyOpen = () => {
    if (!selectedPage || !selectedPage.id) return;
    if (selectedPage.id === "default-page") return;
    if (!isContentPage(selectedPage)) return;
    setIsManualEvalOpen(true);
  };

  const handleEvalManuallySave = async (scores) => {
    if (!selectedPage || !selectedPage.id) return;
    if (!isContentPage(selectedPage)) return;
    try {
      setManualEvalSaving(true);
      await createTopicEvalManually({
        key: topicKey,
        pageId: selectedPage.id,
        evalution_score: scores,
      });
      setIsManualEvalOpen(false);
      await fetchEvalStatus();
      await handlePageSelect(selectedPage);
    } catch (err) {
      console.error("Error saving manual eval:", err);
    } finally {
      setManualEvalSaving(false);
    }
  };

  const fetchPages = async () => {
    try {
      const data = await getPagesForTopic(topicKey);

      if (Array.isArray(data)) {
        setPages(data);
      } else if (
        data &&
        typeof data === "object" &&
        data.hasOwnProperty("id")
      ) {
        setPages([data]);
      } else {
        setPages([]);
      }
    } catch (err) {
      console.error("Error fetching pages:", err);
    }
  };

  const [evalStatusByPageId, setEvalStatusByPageId] = useState({});

  const getEvalStatusForPage = (page) => {
    if (!page) return null;
    const id = page?.id;
    const pageKey = page?.key;
    const level = page?.level;
    // For virtual test sub-pages check the compound key `{parentId}-{level}`
    const virtualKey = level && id ? `${id}-${level}` : null;
    return (
      (virtualKey && evalStatusByPageId[virtualKey]) ||
      (id && evalStatusByPageId[id]) ||
      (pageKey && evalStatusByPageId[pageKey]) ||
      null
    );
  };

  const isPageEvaluated = (page) => {
    if (!page || !page.id) return false;
    if (page.id === "default-page") return true;
    const status = getEvalStatusForPage(page);
    if (status && typeof status.evaluated !== "undefined")
      return !!status.evaluated;
    if (page.is_eval_content) return true;
    if (page.evalution_score) return true;
    if (
      page.overall_score !== null &&
      typeof page.overall_score !== "undefined"
    )
      return true;
    return false;
  };

  const hasContentToEvaluate = (page) => {
    if (!page) return false;
    const levelStr = (page.level || "").toLowerCase();
    const contentToCheck =
      levelStr === "medium" ? page.medium_content
      : levelStr === "hard" ? page.hard_content
      : page.content;
    
    if (!contentToCheck) return false;
    if (Array.isArray(contentToCheck)) return contentToCheck.length > 0;
    if (typeof contentToCheck === "string") {
      try {
        const parsed = JSON.parse(contentToCheck);
        return Array.isArray(parsed) ? parsed.length > 0 : !!parsed;
      } catch (e) {
        return contentToCheck.trim().length > 0;
      }
    }
    return typeof contentToCheck === "object";
  };

  const isContentPage = (page) => {
    if (!page || !page.id) return false;
    if (page.id === "default-page") return false;
    // Test level sub-pages (easy/medium/hard) are evaluable — level field is merged in
    // from the virtual page on selection; is_virtual may not be present in API response
    if (page.page_type === "test" && page.level) return true;
    if (page.is_virtual) return false;
    if (Array.isArray(page.child_pages) && page.child_pages.length > 0)
      return false;
    const type = (page.page_type || "").toLowerCase();
    if (
      type === "quiz" ||
      type === "test" ||
      type === "active_recall" ||
      type === "materials" ||
      type === "material_item"
    )
      return false;
    return true;
  };

  const normalizePageData = (page, payload, extras = {}) => {
    const merged = { ...(page || {}), ...(payload || {}), ...(extras || {}) };
    const normalizedContent =
      typeof merged.content !== "undefined"
        ? merged.content
        : typeof merged.page_content !== "undefined"
          ? merged.page_content
          : typeof merged.pageContent !== "undefined"
            ? merged.pageContent
            : merged.content;

    const normalizedMediumContent =
      typeof merged.medium_content !== "undefined"
        ? merged.medium_content
        : typeof merged.medium_page_content !== "undefined"
          ? merged.medium_page_content
          : typeof merged.mediumContent !== "undefined"
            ? merged.mediumContent
            : merged.medium_content;

    const normalizedHardContent =
      typeof merged.hard_content !== "undefined"
        ? merged.hard_content
        : typeof merged.hard_page_content !== "undefined"
          ? merged.hard_page_content
          : typeof merged.hardContent !== "undefined"
            ? merged.hardContent
            : merged.hard_content;

    return {
      ...merged,
      content: normalizedContent,
      medium_content: normalizedMediumContent,
      hard_content: normalizedHardContent,
    };
  };

  const fetchEvalStatus = async () => {
    if (role !== "admin") return;
    try {
      const status = await getTopicPageEvalStatus(topicKey);
      setEvalStatusByPageId(status?.byPageId || {});
    } catch (err) {
      console.error("Error fetching eval status for topic pages:", err);
      setEvalStatusByPageId({});
    }
  };

  const handleDeleteButtonClick = (subTopic) => {
    setIsConfirmDeleteOpen(true);
    setSelectedSubTopic(subTopic);
  };

  const handleDelete = async (event) => {
    event.stopPropagation();
    try {
      await deleteSubtopic(selectedSubTopic.key);
      fetchSubTopics();
      setIsConfirmDeleteOpen(false);
    } catch (err) {
      console.error("Error deleting subtopic:", err);
    }
  };

  const handleEdit = (subTopic) => {
    setSelectedSubTopic(subTopic);
    setPopupHeaderTitle(`Edit ${subTopic.name}`);
    setPopupBtnText("Update");
    setPopupInitialValue(subTopic.name);
    setOpenPopup(true);
  };

  const handleUpdate = async (updatedName, totalNumberQuizes) => {
    try {
      const updatedSubTopic = {
        ...selectedSubTopic,
        name: updatedName,
        total_number_quizes: totalNumberQuizes ? parseInt(totalNumberQuizes, 10) : 0,
      };

      await updateSubtopic(updatedSubTopic);
      fetchSubTopics();
    } catch (err) {
      console.error("Error updating subtopic:", err);
    }
    setOpenPopup(false);
    setPopupInitialValue("");
    setSelectedSubTopic(null);
  };

  const handleSubtopicClick = (subTopic) => {
    if (role === "admin") {
      if (isEvalPath) {
        navigate(
          `/admin/eval/${key}/subject/${subkey}/topic/${topicKey}/subTopic/${subTopic.key}`,
        );
      } else {
        navigate(
          `/admin/course/${key}/subject/${subkey}/topic/${topicKey}/subtopic/${subTopic.key}`,
        );
      }
    } else {
      navigate(
        `/institution/course/${key}/subject/${subkey}/topic/${topicKey}/subtopic/${subTopic.key}`,
      );
    }
  };

  const handleClosePopup = () => {
    setOpenPopup(false);
    setPopupInitialValue("");
    setSelectedSubTopic(null);
  };

  const handleAddPage = () => {
    fetchPages();
  };

  const handleEvalWithAi = async () => {
    if (!selectedPage || !selectedPage.id) return;
    if (selectedPage.id === "default-page") return;
    if (!isContentPage(selectedPage)) return;
    try {
      setLoading(true);
      const level = selectedPage?.level || null;
      // For virtual test sub-pages, use the parent page id (strip the level suffix)
      const effectivePageId = selectedPage?.is_virtual && level
        ? selectedPage.id
        : selectedPage.id;
      const aiResp = await evalWithAiMark({
        title: "topic",
        key: topicKey,
        pageId: effectivePageId,
        level,
      });
      const aiPayload = aiResp?.data ?? aiResp;

      let evalution_score =
        aiPayload?.structured?.evalution_score ??
        aiPayload?.structured?.evalutionScore ??
        null;

      if (
        !evalution_score &&
        aiPayload?.content &&
        typeof aiPayload.content === "string"
      ) {
        try {
          const parsed = JSON.parse(aiPayload.content);
          evalution_score =
            parsed?.evalution_score ??
            parsed?.evalutionScore ??
            parsed?.evaluation_score ??
            parsed?.evaluationScore ??
            parsed?.scores ??
            null;
        } catch (e) {
          // ignore
        }
      }

      if (!evalution_score) {
        evalution_score =
          aiPayload?.evalution_score ??
          aiPayload?.evalutionScore ??
          aiPayload?.evaluation_score ??
          aiPayload?.evaluationScore ??
          aiPayload?.scores ??
          aiPayload?.result?.evalution_score ??
          aiPayload?.result?.evalutionScore ??
          aiPayload?.result?.evaluation_score ??
          aiPayload?.result?.evaluationScore ??
          aiPayload?.result?.scores ??
          null;
      }

      if (evalution_score) {
        await createTopicEvalManually({
          key: topicKey,
          pageId: selectedPage.id,
          evalution_score,
        });
      }
      await fetchEvalStatus();
      await handlePageSelect(selectedPage);
    } catch (err) {
      console.error("Error running eval with AI:", err);
    } finally {
      setLoading(false);
    }
  };

  const handlePageSelect = async (page) => {
    if (!page || !page.id || !isEvalPath) {
      setSelectedPage(page);
      return;
    }

    if (!isContentPage(page)) {
      setSelectedPage(page);
      return;
    }

    const status = getEvalStatusForPage(page);
    if (status && status.evaluated && page.id !== "default-page") {
      try {
        setLoading(true);
        const evalData = await getTopicEvalPageContent(
          status?.evaluated_id || page.id,
        );
        const payload = evalData?.data ?? evalData;
        if (payload) {
          const overall_score =
            payload.overall_score ?? payload.overallScore ?? null;
          const evalution_score =
            payload.evalution_score ??
            payload.evalutionScore ??
            payload.evaluation_score ??
            payload.evaluationScore ??
            payload.scores ??
            null;
          setSelectedPage(
            normalizePageData(page, payload, {
              page_type: payload.page_type || page.page_type,
              is_eval_content: true,
              overall_score,
              evalution_score,
            }),
          );
        } else {
          setSelectedPage(page);
        }
      } catch (err) {
        console.error("Error fetching eval content:", err);
        setSelectedPage(page);
      } finally {
        setLoading(false);
      }
    } else {
      if (page.id === "default-page") {
        setSelectedPage(page);
        return;
      }

      setSelectedPage(
        normalizePageData(page, null, {
          is_eval_content: false,
        }),
      );
    }
  };

  const handleOpenEvalScoreDetails = async () => {
    if (!isEvalPath) return;
    if (!selectedPage || !selectedPage.id) return;
    if (selectedPage.id === "default-page") return;
    if (!isContentPage(selectedPage)) return;

    const status = getEvalStatusForPage(selectedPage);
    const evaluated = isPageEvaluated(selectedPage);
    if (!evaluated) return;

    try {
      setLoading(true);
      const evalData = await getTopicEvalPageContent(
        status?.evaluated_id || selectedPage.id,
      );
      const payload = evalData?.data ?? evalData;

      if (payload) {
        const overall_score =
          payload.overall_score ?? payload.overallScore ?? null;
        const evalution_score =
          payload.evalution_score ??
          payload.evalutionScore ??
          payload.evaluation_score ??
          payload.evaluationScore ??
          payload.scores ??
          null;

        setSelectedPage((prev) =>
          normalizePageData(prev || selectedPage, payload, {
            page_type: payload.page_type || (prev || selectedPage)?.page_type,
            is_eval_content: true,
            overall_score,
            evalution_score,
          }),
        );
      }

      setIsEvalScorePopupOpen(true);
    } catch (err) {
      console.error("Error fetching eval content:", err);
      setIsEvalScorePopupOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const renderEvalScoreChips = () => {
    if (!isEvalPath || !selectedPage || !selectedPage.id) return null;
    if (selectedPage.id === "default-page") return null;
    if (!isContentPage(selectedPage)) return null;

    const status = getEvalStatusForPage(selectedPage);
    const evaluated = isPageEvaluated(selectedPage);
    if (!evaluated) return null;

    const scores = selectedPage.evalution_score || {};
    const overall =
      status &&
      status.overall_score !== null &&
      typeof status.overall_score !== "undefined"
        ? status.overall_score
        : selectedPage.overall_score !== null &&
            typeof selectedPage.overall_score !== "undefined"
          ? selectedPage.overall_score
          : scores.evalution_score;

    const items = [
      { key: "evalution_score", value: overall },
      { key: "quality_score", value: scores.quality_score },
      { key: "relevance_score", value: scores.relevance_score },
      { key: "grounding_score", value: scores.grounding_score },
      { key: "reasoning_score", value: scores.reasoning_score },
      { key: "risk_score", value: scores.risk_score },
    ];

    const formatLabel = (raw) => {
      if (!raw) return "";
      return String(raw)
        .replace(/_/g, " ")
        .split(" ")
        .filter(Boolean)
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
        .join(" ");
    };

    const toPct = (val) => {
      const num = Number(val);
      if (!Number.isFinite(num)) return 0;
      return Math.max(0, Math.min(100, (num / 10) * 100));
    };

    return (
      <div className="eval-score-actions">
        <button
          type="button"
          className="primary-btn eval-view-details-btn"
          onClick={handleOpenEvalScoreDetails}
        >
          View Evaluate Details
        </button>

        <EvalScoreDetailsPopup
          open={isEvalScorePopupOpen}
          onClose={() => setIsEvalScorePopupOpen(false)}
          overall={overall}
          scores={scores}
        />
      </div>
    );
  };

  const handleCreateSubTopicSuccess = (subTopicData) => {
    fetchSubTopics();
  };

  const handleUpdatePageContent = (updatedPageData) => {
    setRemoveCache(true);
    setSelectedPage(updatedPageData);
  };

  // After AI generation saves, refresh pages list and eval status
  const handleGeneratorSave = async (updatedPageData) => {
    handleUpdatePageContent(updatedPageData);
    try {
      await fetchPages();
      await fetchEvalStatus();
      setTimeout(() => fetchEvalStatus(), 3000);
      setTimeout(() => fetchEvalStatus(), 6000);
      setTimeout(() => fetchEvalStatus(), 10000);
    } catch (err) {
      console.error("Error refreshing after generator save:", err);
    }
  };

  const handleRemoveMaterial = async (materialId) => {
    if (!selectedPage || !selectedPage.id) return;

    try {
      setLoading(true);

      await removePageContentFile({
        pageid: selectedPage.id,
        material_id: materialId,
        course_key: key,
        subject_key: subkey,
        topic_key: topicKey,
      });

      const updatedPageData = await getPageForTopic(
        topicKey,
        selectedPage.id,
        selectedPage.level,
      );

      setSelectedPage(updatedPageData);
      setRemoveCache(true);

      await fetchPages();
    } catch (error) {
      console.error("Error removing material:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleUploadMaterial = async (materialData) => {
    if (!selectedPage || !selectedPage.id) return;

    try {
      setLoading(true);
      const uploadResponse = await uploadPageContentFile({
        title: "topic",
        key: materialData.key,
        pageid: materialData.pageid,
        type: materialData.type,
        file: materialData.file,
        url: materialData.url,
        name: materialData.name,
      });

      const updatedPageData = await getPageForTopic(
        topicKey,
        selectedPage.id,
        selectedPage.level,
      );

      setSelectedPage(updatedPageData);
      setRemoveCache(true);

      await fetchPages();
    } catch (error) {
      console.error("Error uploading material:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setSelectedPage(null);
    fetchSubTopics();
    fetchPages();
    fetchEvalStatus();
  }, [topicKey]);

  useEffect(() => {
    setSelectedChildMaterial(null);
  }, [selectedPage]);

  const hasWritePermission = !isEvalPath && (
    role === "admin" ||
    ((role === "institution" || role === "teacher") && !selectedPage?.is_admin_page)
  );

  return (
    <>
      {loading && <GlobalLoader visible={loading} text="Loading..." />}
      <div className="subtopics-container">
        <div className="subtopics-content">
          <div className="subtopic-form-container"></div>

          <div className="subtopics-list-container">
            {(() => {
              if (role !== "admin") return null;
              if (!selectedPage || !selectedPage.id) return null;
              if (selectedPage.id === "default-page") return null;
              if (!isContentPage(selectedPage)) return null;
              if (isPageEvaluated(selectedPage)) return null;
              if (!hasContentToEvaluate(selectedPage)) return null;

              return (
                <div className="eval-actions-bar">
                  <button
                    type="button"
                    className="eval-btn"
                    onClick={handleEvalWithAi}
                  >
                    Evaluate with AI
                  </button>
                </div>
              );
            })()}

            {renderEvalScoreChips()}

            <EvalManualPopup
              open={isManualEvalOpen}
              onClose={() => setIsManualEvalOpen(false)}
              onSave={handleEvalManuallySave}
              saving={manualEvalSaving}
            />

            {selectedPage &&
            (selectedPage?.content ||
              selectedPage?.medium_content ||
              selectedPage?.hard_content ||
              selectedPage?.page_type === "material_item" ||
              selectedPage?.page_type === "materials") ? (
              (() => {
                if (selectedPage.page_type === "material_item") {
                  return (
                    <ContentToolbarWrapperComponent
                      onEdit={undefined}
                      onAdd={undefined}
                      onUploadMaterials={
                        isEvalPath
                          ? undefined
                          : () => setIsUploadMaterialOpen(true)
                      }
                      pageType={selectedPage?.page_type}
                      hasContent={Boolean(selectedPage?.url || selectedPage?.file || selectedPage?.material_url || selectedPage?.name)}
                      materialType={selectedPage?.material_type || ""}
                      className={
                        role === "admin" &&
                        !!selectedPage?.id &&
                        selectedPage?.id !== "default-page" &&
                        isContentPage(selectedPage) &&
                        !isPageEvaluated(selectedPage) &&
                        hasContentToEvaluate(selectedPage)
                          ? "has-eval-actions"
                          : ""
                      }
                    >
                      <div className="content-wrapper">
                        <MaterialViewer
                          material={selectedPage}
                          pageData={selectedPage}
                          courseKey={key}
                          subjectKey={subkey}
                          topicKey={topicKey}
                          onRemove={handleRemoveMaterial}
                          title="topic"
                        />
                      </div>
                    </ContentToolbarWrapperComponent>
                  );
                }

                let contentArray = [];

                try {
                  let contentField = selectedPage?.content;
                  const level = (selectedPage?.level || "").toLowerCase();
                  if (level === "medium" && selectedPage?.medium_content) {
                    contentField = selectedPage?.medium_content;
                  } else if (level === "hard" && selectedPage?.hard_content) {
                    contentField = selectedPage?.hard_content;
                  }

                  if (contentField) {
                    if (typeof contentField === "string") {
                      const trimmed = contentField.trim();
                      if (trimmed) {
                        const parsedContent = JSON.parse(trimmed);
                        if (Array.isArray(parsedContent)) {
                          contentArray = parsedContent;
                        } else if (parsedContent) {
                          contentArray = [parsedContent];
                        }
                      }
                    } else if (Array.isArray(contentField)) {
                      contentArray = contentField;
                    } else if (typeof contentField === "object") {
                      contentArray = [contentField];
                    }
                  }
                } catch (e) {
                  contentArray = [{ content: selectedPage?.content }];
                }

                const hasContent = contentArray && contentArray.length > 0;

                return (
                  <ContentToolbarWrapperComponent
                    onEdit={
                      isEvalPath
                        ? undefined
                        : selectedPage?.page_type === "quiz" ||
                            selectedPage?.page_type === "mcq"
                          ? undefined
                          : () => setIsEditorOpen(true)
                    }
                    onAdd={
                      isEvalPath
                        ? undefined
                        : () => {
                            setIsReplaceMode(false);
                            setIsGeneratorOpen(true);
                          }
                    }
                    onReplace={
                      isEvalPath
                        ? undefined
                        : () => {
                            setIsReplaceMode(true);
                            setIsGeneratorOpen(true);
                          }
                    }
                    onUploadMaterials={
                      isEvalPath || selectedPage?.page_type !== "material_item"
                        ? undefined
                        : () => setIsUploadMaterialOpen(true)
                    }
                    pageType={selectedPage?.page_type}
                    hasContent={hasContent}
                    disableAdd={selectedPage?.page_type === "quiz"}
                    permission={hasWritePermission}
                    materialType={selectedPage?.material_type || ""}
                    audioBook={selectedPage?.tts_s3_url || null}
                    layer={"topic"}
                    id={selectedPage?.id}
                    className={
                      role === "admin" &&
                      !!selectedPage?.id &&
                      selectedPage?.id !== "default-page" &&
                      isContentPage(selectedPage) &&
                      !isPageEvaluated(selectedPage) &&
                      hasContentToEvaluate(selectedPage)
                        ? "has-eval-actions"
                        : ""
                    }
                  >
                    <div className="content-wrapper">
                      {contentArray?.length > 0 ? (
                        contentArray.map((item, index) => {
                          let contentType = "content";
                          let contentToDisplay = item;
                          let itemName = item.name || "Material";

                          let parsedItem = item;
                          if (typeof item === "string") {
                            try {
                              parsedItem = JSON.parse(item);
                              itemName = parsedItem.name || itemName;
                            } catch (e) {
                              parsedItem = item;
                            }
                          }

                          if (
                            parsedItem &&
                            typeof parsedItem === "object" &&
                            parsedItem.type
                          ) {
                            contentType = parsedItem.type;
                            contentToDisplay = parsedItem.content || parsedItem;
                            itemName = parsedItem.name || itemName;
                          } else if (
                            item &&
                            typeof item === "object" &&
                            item.type
                          ) {
                            contentType = item.type;
                            contentToDisplay = item.content;
                            itemName = item.name || itemName;
                          } else if (
                            item &&
                            typeof item === "object" &&
                            item.content
                          ) {
                            contentToDisplay = item.content;
                            itemName = item.name || itemName;
                          }

                          switch (contentType) {
                            case "material_item":
                            case "pdf":
                            case "video":
                            case "audio":
                            case "image":
                            case "ppt":
                            case "doc":
                              return (
                                <div key={`material-${index}`}>
                                  <MaterialViewer
                                    material={item}
                                    pageData={selectedPage}
                                    courseKey={key}
                                    subjectKey={subkey}
                                    topicKey={topicKey}
                                    onRemove={handleRemoveMaterial}
                                    title="topic"
                                  />
                                </div>
                              );
                            case "mcq":
                              return (
                                <MCQViewer
                                  key={`mcq-${index}`}
                                  questionData={contentToDisplay}
                                  onUpdate={handleUpdatePageContent}
                                  pageId={selectedPage?.id}
                                  pageData={selectedPage}
                                  context="topic"
                                  courseKey={key}
                                  subjectKey={subkey}
                                  topicKey={topicKey}
                                  contentIndex={index}
                                  onAddNext={() => {
                                    setAddAfterIndex(index);
                                    setIsGeneratorOpen(true);
                                  }}
                                  permission={hasWritePermission}
                                />
                              );
                            case "trueorfalse":
                              return (
                                <TrueFalseViewer
                                  key={`tf-${index}`}
                                  questionData={contentToDisplay}
                                  onUpdate={handleUpdatePageContent}
                                  pageId={selectedPage?.id}
                                  pageData={selectedPage}
                                  context="topic"
                                  courseKey={key}
                                  subjectKey={subkey}
                                  topicKey={topicKey}
                                  contentIndex={index}
                                  onAddNext={() => {
                                    setAddAfterIndex(index);
                                    setIsGeneratorOpen(true);
                                  }}
                                  permission={hasWritePermission}
                                />
                              );
                            case "match":
                              return (
                                <MatchViewer
                                  key={`match-${index}`}
                                  questionData={contentToDisplay}
                                  onUpdate={handleUpdatePageContent}
                                  pageId={selectedPage?.id}
                                  pageData={selectedPage}
                                  context="topic"
                                  courseKey={key}
                                  subjectKey={subkey}
                                  topicKey={topicKey}
                                  contentIndex={index}
                                  onAddNext={() => {
                                    setAddAfterIndex(index);
                                    setIsGeneratorOpen(true);
                                  }}
                                  permission={hasWritePermission}
                                />
                              );
                            case "fillups":
                              return (
                                <FillUpsViewer
                                  key={`fillups-${index}`}
                                  questionData={contentToDisplay}
                                  onUpdate={handleUpdatePageContent}
                                  pageId={selectedPage?.id}
                                  pageData={selectedPage}
                                  context="topic"
                                  courseKey={key}
                                  subjectKey={subkey}
                                  topicKey={topicKey}
                                  contentIndex={index}
                                  onAddNext={() => {
                                    setAddAfterIndex(index);
                                    setIsGeneratorOpen(true);
                                  }}
                                  permission={hasWritePermission}
                                />
                              );
                            case "analysis":
                              return (
                                <AnalysisComponent
                                  key={`analysis-${index}`}
                                  data={contentToDisplay}
                                  title="Conversational Analysis"
                                  onUpdate={handleUpdatePageContent}
                                  pageId={selectedPage?.id}
                                  pageData={selectedPage}
                                  context="topic"
                                  courseKey={key}
                                  subjectKey={subkey}
                                  topicKey={topicKey}
                                  contentIndex={index}
                                  onAddNext={() => {
                                    setAddAfterIndex(index);
                                    setIsGeneratorOpen(true);
                                  }}
                                  permission={hasWritePermission}
                                />
                              );
                            default:
                              return (
                                <MarkdownViewer
                                  key={index}
                                  content={
                                    typeof contentToDisplay === "string"
                                      ? contentToDisplay
                                      : JSON.stringify(contentToDisplay)
                                  }
                                  pageId={selectedPage?.id}
                                  context="topic"
                                  courseKey={key}
                                  subjectKey={subkey}
                                  topicKey={topicKey}
                                  onUpdate={handleUpdatePageContent}
                                />
                              );
                          }
                        })
                      ) : (
                        <>
                          <EmptyPage content={"No content Available"} />
                        </>
                      )}
                    </div>
                  </ContentToolbarWrapperComponent>
                );
              })()
            ) : (
              <>
                {subTopics?.length > 0 ? (
                  <div className="subtopics-card">
                    <div className="subtopics-card-header">
                      <span>Name of the subtopics</span>
                      {role === "admin" && !isEvalPath && (
                        <span style={{ marginRight: "16px" }}>Actions</span>
                      )}
                    </div>
                    <ul className="subtopics-list">
                      {subTopics.map((top) => (
                        <li key={top.id} className="subtopic-item">
                          <span
                            className="subtopic-name"
                            onClick={() => handleSubtopicClick(top)}
                          >
                            {top.name}
                          </span>
                          {role === "admin" && !isEvalPath && (
                            <div className="subtopic-actions">
                              <span
                                className="action-link"
                                onClick={() => handleEdit(top)}
                              >
                                Edit
                              </span>
                              <span
                                className="action-link"
                                onClick={() => handleDeleteButtonClick(top)}
                              >
                                Delete
                              </span>
                            </div>
                          )}
                        </li>
                      ))}
                    </ul>
                    {role === "admin" && (
                      <div className="subtopics-card-footer">
                        <ActionButtons
                          context="subtopic"
                          courseKey={key}
                          subjectKey={subkey}
                          topicKey={topicKey}
                          onCreateSuccess={handleCreateSubTopicSuccess}
                          buttonLabel="Add Sub Topic"
                        />
                      </div>
                    )}
                  </div>
                ) : (
                  <>
                    <EmptyPage content={"No subtopics Available"} />
                    {!isEvalPath && (
                      <div className="subtopics-card-footer">
                        <ActionButtons
                          context="subtopic"
                          courseKey={key}
                          subjectKey={subkey}
                          topicKey={topicKey}
                          onCreateSuccess={handleCreateSubTopicSuccess}
                          buttonLabel="Add Sub Topic"
                        />
                      </div>
                    )}
                  </>
                )}
              </>
            )}
          </div>

          <div className="pages-component-container">
            <div className="pages-component">
              <PagesComponent
                pages={pages}
                onAddPage={isEvalPath ? undefined : handleAddPage}
                topicKey={topicKey}
                onPageSelect={handlePageSelect}
                selectedPageData={selectedPage}
                removeCache={removeCache}
                setRemoveCache={setRemoveCache}
                readOnly={isEvalPath}
                evalStatusByPageId={evalStatusByPageId}
              />
            </div>
          </div>
        </div>

        {isConfirmDeleteOpen && !isEvalPath && (
          <DeleteConfirmPopUp
            isConfirmDeleteOpen={isConfirmDeleteOpen}
            setIsConfirmDeleteOpen={setIsConfirmDeleteOpen}
            handleDelete={handleDelete}
          />
        )}
        {openPopup && !isEvalPath && (
          <CreateCoursePopup
            open={openPopup}
            handleClose={handleClosePopup}
            title="Sub Topic"
            headerTitle={popupHeaderTitle}
            buttonText={popupBtnText}
            initialValue={popupInitialValue}
            initialTotalNumberQuizes={selectedSubTopic ? selectedSubTopic.total_number_quizes : ""}
            onSave={handleUpdate}
          />
        )}

        {isGeneratorOpen && !isEvalPath && (
          <ContentGeneratorPopup
            open={isGeneratorOpen}
            handleClose={() => {
              setIsGeneratorOpen(false);
              setAddAfterIndex(null);
              setIsReplaceMode(false);
            }}
            onSave={handleGeneratorSave}
            isReplaceMode={isReplaceMode}
            existingContent={
              (selectedPage?.level === "medium"
                ? selectedPage?.medium_content
                : selectedPage?.level === "hard"
                ? selectedPage?.hard_content
                : selectedPage?.content) || ""
            }
            pageId={selectedPage?.id || null}
            pageData={selectedPage || null}
            context="topic"
            courseKey={key}
            subjectKey={subkey}
            topicKey={topicKey}
            insertionIndex={
              typeof addAfterIndex === "number"
                ? addAfterIndex
                : Array.isArray(selectedPage?.content)
                  ? selectedPage?.content.length - 1
                  : -1
            }
          />
        )}

        {isAudioGenerated && (
          <SuccessPopup
            open={isAudioGenerated}
            onClose={() => setIsAudioGenerated(false)}
            iconSrc={checkImg}
          />
        )}

        {isEditorOpen && !isEvalPath && (
          <MarkdownEditorPopup
            open={isEditorOpen}
            handleClose={() => setIsEditorOpen(false)}
            title="Edit Content"
            initialValue={selectedPage?.content || ""}
            onSave={(updatedPageData) => {
              handleUpdatePageContent(updatedPageData);
              setIsEditorOpen(false);
            }}
            context="topic"
            pageId={selectedPage?.id || null}
            pageData={selectedPage || null}
            courseKey={key}
            subjectKey={subkey}
            topicKey={topicKey}
          />
        )}

        {isUploadMaterialOpen && !isEvalPath && (
          <UploadMaterialPopup
            open={isUploadMaterialOpen}
            handleClose={() => setIsUploadMaterialOpen(false)}
            onSave={handleUploadMaterial}
            materialType={selectedPage?.material_type || ""}
            pageData={
              selectedPage?.page_type === "materials"
                ? selectedChildMaterial || selectedPage
                : selectedPage
            }
            context="topic"
            courseKey={key}
            subjectKey={subkey}
            topicKey={topicKey}
          />
        )}

        <ViewMaterialPopup
          open={isMaterialPopupOpen}
          handleClose={() => setIsMaterialPopupOpen(false)}
          material={selectedMaterialForPopup}
        />
      </div>
    </>
  );
};

export default TopicDetails;
